import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';

import { API_URL, enrichedFetch } from '../../services/api';
import { fetchTokens } from '../../services/auth';
import { Project } from '../../Types/dealGPT';
import {
  AccountType,
  CreateFolder,
  Folder,
  FolderResponse,
  OpportunityType,
  UpdateFolder,
} from '../../Types/folder';
import { withRetry } from '../../util/withRetry';
import { FOLDER_LIST_SIZE } from '../consts';
import { setPage } from '../reducers/folderReducer';
import { AppDispatch, RootState } from '../store';

let folderController: AbortController | null = null;

export const getFolders = createAsyncThunk<
  FolderResponse,
  {
    pageAction?: 'increment' | 'reset';
    foldersWithProjects?: boolean;
    searchQuery?: string;
  } | void,
  { state: RootState }
>('folders/fetchFolders', async (arg, { getState, dispatch, rejectWithValue }) => {
  // Cancel any ongoing request
  if (folderController) {
    folderController.abort('Duplicate request: Aborting previous folder fetch');
  }

  // Create a new controller for the current request
  folderController = new AbortController();
  const signal = folderController.signal;

  const { page, searchQuery: stateSearchQuery } = getState().folders;
  const effectiveQuery = (arg?.searchQuery ?? stateSearchQuery).trim();
  const pageAction = arg?.pageAction;
  const foldersWithProjects = arg?.foldersWithProjects ?? false;

  let newPage = page;
  if (pageAction === 'increment') {
    newPage += 1;
  } else if (pageAction === 'reset') {
    newPage = 1;
  }
  const pageOffset = FOLDER_LIST_SIZE * (newPage - 1);

  dispatch(setPage(newPage));

  try {
    return await withRetry(
      async () => {
        // Respect abort before attempting
        if (signal.aborted) {
          throw new Error('Fetch aborted');
        }

        let url = `${API_URL}/folders?offset=${pageOffset}&foldersWithProjects=${foldersWithProjects}`;
        if (effectiveQuery.trim().length > 0) {
          url += `&q=${effectiveQuery}`;
        }

        const response = await enrichedFetch(url, { signal });

        if (response instanceof Response && !response.ok) {
          throw new Error(`HTTP error, response: ${response.statusText}`);
        }

        return response as FolderResponse;
      },
      {
        label: 'Fetching folders',
        onRetry: async (err) => {
          // Skip retry if aborted
          if ((err as Error)?.message === 'Fetch aborted' || signal.aborted) {
            throw new Error('Fetch aborted');
          }

          await fetchTokens();
        },
      }
    );
  } catch (error) {
    // Always reset controller when finished
    folderController = null;

    // Special case: fetch aborted → return empty, don’t reject
    if ((error as Error)?.message === 'Fetch aborted') {
      return { unpinnedFolders: [], pinnedFolders: [], totalCount: 0 };
    }
    return rejectWithValue('Failed to fetch folders');
  } finally {
    // Ensure controller is cleared even on success
    folderController = null;
  }
});

// Created a separate version of getFolders for search inputs, so changes here won’t affect the default folder items.
// ⚠️ If getFolders is updated, make sure to update this version as well to keep them in sync.
export const getMoveToFolders = createAsyncThunk<
  FolderResponse,
  {
    pageAction?: 'increment' | 'reset';
    foldersWithProjects?: boolean;
    searchQuery?: string;
  } | void,
  { state?: RootState }
>('folders/fetchMoveToFolders', async (arg, { rejectWithValue }) => {
  const foldersWithProjects = arg?.foldersWithProjects ?? false;
  const searchQuery = arg?.searchQuery;

  try {
    let url = `${API_URL}/folders?offset=0&foldersWithProjects=${foldersWithProjects}`;
    if (searchQuery) {
      url += `&q=${searchQuery}`;
    }
    const response = await enrichedFetch(url);
    if (response instanceof Response && !response.ok) {
      throw new Error(`HTTP error, response: ${response.statusText}`);
    }
    return response as FolderResponse;
  } catch {
    return rejectWithValue('Failed to fetch folders');
  }
});

export const getFolder = createAsyncThunk<Folder, { folderId: Folder['id']; isAdmin?: boolean }>(
  'folders/getFolder',
  async ({ folderId, isAdmin }, { rejectWithValue }) => {
    try {
      return await withRetry(
        async () => {
          let url = `${API_URL}/folders/${folderId}`;

          if (isAdmin) {
            url += `?isAdmin=${isAdmin}`;
          }
          const response = (await enrichedFetch(url)) as Folder;
          return response;
        },
        {
          label: 'Fetching folder',
          onRetry: fetchTokens,
        }
      );
    } catch (error) {
      enqueueSnackbar('Failed to fetch folder', { variant: 'error' });
      return rejectWithValue('Failed to fetch folder');
    }
  }
);

export const getFolderFromProjectId = createAsyncThunk<Folder, Project['id']>(
  'folders/getFolderFromProjectId',
  async (id, { rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(`${API_URL}/folders/project/${id}`)) as Folder;
      return response;
    } catch {
      enqueueSnackbar('Failed to fetch folder', { variant: 'error' });
      return rejectWithValue('Failed to fetch folder');
    }
  }
);

export const getAccountsAndOpportunities = createAsyncThunk<
  { accounts: AccountType[]; opportunities: OpportunityType[] },
  void,
  { state: RootState }
>('folders/getAccountsAndOpportunities', async (_, { rejectWithValue }) => {
  try {
    const response = (await enrichedFetch(`${API_URL}/folders/accounts-and-opportunities`)) as {
      accounts: AccountType[];
      opportunities: OpportunityType[];
    };
    return response;
  } catch {
    enqueueSnackbar('Failed to fetch accounts and opportunities', { variant: 'error' });
    return rejectWithValue('Failed to fetch accounts and opportunities');
  }
});

export const postFolder = createAsyncThunk<Folder, CreateFolder, { dispatch: AppDispatch }>(
  'folders/postFolder',
  async (folder, { rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(`${API_URL}/folders`, {
        method: 'POST',
        body: JSON.stringify(folder),
        headers: {
          'Content-Type': 'application/json',
        },
      })) as Folder;
      return response;
    } catch {
      enqueueSnackbar('Failed to post folder', { variant: 'error' });
      return rejectWithValue('Failed to post folder');
    }
  }
);

export const updateFolder = createAsyncThunk<
  Folder,
  { updatedFields: UpdateFolder; folderId: Folder['id'] },
  { dispatch: AppDispatch }
>('folders/updateFolder', async ({ updatedFields, folderId }, { rejectWithValue }) => {
  try {
    const folder = (await enrichedFetch(`${API_URL}/folders/${folderId}`, {
      method: 'PUT',
      body: JSON.stringify(updatedFields),
      headers: {
        'Content-Type': 'application/json',
      },
    })) as Folder;

    return folder;
  } catch {
    enqueueSnackbar('Failed to update folder', { variant: 'error' });
    return rejectWithValue('Failed to update folder');
  }
});

export const deleteFolder = createAsyncThunk<
  void,
  { folderId: Folder['id']; deleteConversations: boolean; newFolderId?: Folder['id'] },
  { dispatch: AppDispatch }
>(
  'folders/deleteFolder',
  async ({ folderId, deleteConversations, newFolderId }, { rejectWithValue }) => {
    try {
      let url = `${API_URL}/folders/${folderId}`;

      if (deleteConversations !== undefined) {
        url += `?deleteConversations=${deleteConversations}`;
      }
      if (newFolderId) {
        url += `&newFolderId=${newFolderId}`;
      }

      await enrichedFetch(url, {
        method: 'DELETE',
      });
    } catch {
      enqueueSnackbar('Failed to delete folder', { variant: 'error' });
      return rejectWithValue('Failed to delete folder');
    }
  }
);

export const duplicateFolder = createAsyncThunk<
  Folder,
  { folderId: Folder['id'] },
  { dispatch: AppDispatch }
>('folders/duplicateFolder', async ({ folderId }, { rejectWithValue }) => {
  try {
    const response = (await enrichedFetch(`${API_URL}/folders/${folderId}/duplicate`, {
      method: 'POST',
    })) as Folder;
    return response;
  } catch {
    enqueueSnackbar('Failed to duplicate folder', { variant: 'error' });
    return rejectWithValue('Failed to duplicate folder');
  }
});
